import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Textarea } from '@/shared/components/ui/textarea';
import { toast } from 'sonner';
import {
  FileText, Plus, RefreshCw, Search, Eye, Send, MoreHorizontal,
  DollarSign, Calendar, User, CheckCircle, XCircle, Trash2 } from
'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from
'@/shared/components/ui/dropdown-menu';

const API_URL = API_BASE;
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('ezerpToken')}`
});

const statusColors = {
  draft: 'bg-gray-500/20 text-gray-400',
  submitted: 'bg-blue-500/20 text-blue-400',
  approved: 'bg-cyan-500/20 text-cyan-400',
  posted: 'bg-green-500/20 text-green-400',
  partial: 'bg-amber-500/20 text-amber-400',
  paid: 'bg-purple-500/20 text-purple-400',
  voided: 'bg-red-500/20 text-red-400'
};

export const ARInvoicesPage = () => {const { t } = useTranslation("finance");
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showDetails, setShowDetails] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    invoice_type: 'standard',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    currency: 'USD',
    notes: '',
    lines: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 0 }]
  });

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/finance/ar/invoices?`;
      if (statusFilter !== 'all') url += `status=${statusFilter}&`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch invoices');
      const data = await res.json();
      setInvoices(Array.isArray(data) ? data : data.invoices || []);
    } catch (err) {
      toast.error(err.message);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finance/ar/customers`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setCustomers(Array.isArray(data) ? data : data.customers || []);
      }
    } catch (err) {console.error(err);}
  };

  useEffect(() => {fetchInvoices();fetchCustomers();}, [statusFilter]);
  useEffect(() => {const timer = setTimeout(fetchInvoices, 300);return () => clearTimeout(timer);}, [search]);

  const handleCreate = async () => {
    try {
      const payload = {
        ...formData,
        customer_id: parseInt(formData.customer_id),
        lines: formData.lines.filter((l) => l.description).map((l) => ({
          ...l,
          quantity: parseFloat(l.quantity) || 1,
          unit_price: parseFloat(l.unit_price) || 0,
          tax_rate: parseFloat(l.tax_rate) || 0
        }))
      };
      const res = await fetch(`${API_URL}/api/finance/ar/invoices`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to create invoice');
      toast.success('Invoice created');
      setShowCreate(false);
      setFormData({
        customer_id: '', invoice_type: 'standard',
        issue_date: new Date().toISOString().split('T')[0],
        due_date: '', currency: 'USD', notes: '',
        lines: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 0 }]
      });
      fetchInvoices();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handlePost = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/finance/ar/invoices/${id}/post`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to post invoice');
      toast.success('Invoice posted');
      fetchInvoices();
    } catch (err) {toast.error(err.message);}
  };

  const handleVoid = async (id) => {
    if (!window.confirm('Are you sure you want to void this invoice?')) return;
    try {
      const res = await fetch(`${API_URL}/api/finance/ar/invoices/${id}/void`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to void invoice');
      toast.success('Invoice voided');
      fetchInvoices();
    } catch (err) {toast.error(err.message);}
  };

  const viewDetails = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/finance/ar/invoices/${id}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch details');
      setShowDetails(await res.json());
    } catch (err) {toast.error(err.message);}
  };

  const addLine = () => {
    setFormData((prev) => ({
      ...prev,
      lines: [...prev.lines, { description: '', quantity: 1, unit_price: 0, tax_rate: 0 }]
    }));
  };

  const updateLine = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      lines: prev.lines.map((line, i) => i === index ? { ...line, [field]: value } : line)
    }));
  };

  const subtotal = formData.lines.reduce((sum, l) => sum + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0), 0);
  const totalOutstanding = invoices.reduce((sum, i) => sum + (i.amount_due || 0), 0);
  const overdueCount = invoices.filter((i) => i.status === 'posted' && new Date(i.due_date) < new Date()).length;

  return (
    <div className="space-y-6" data-testid="ar-invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("arInvoices", "AR Invoices")}</h1>
          <p className="text-muted-foreground mt-1">{t("manageAccountsReceivable", "Manage accounts receivable invoices")}</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2 bg-green-600 hover:bg-green-700">
          <Plus className="h-4 w-4" />{t("newInvoice", "New Invoice")}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalInvoices", "Total Invoices")}</p>
                <p className="text-3xl font-bold">{invoices.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/20"><FileText className="h-6 w-6 text-blue-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("outstanding", "Outstanding")}</p>
                <p className="text-3xl font-bold">${totalOutstanding.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/20"><DollarSign className="h-6 w-6 text-green-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("overdue", "Overdue")}</p>
                <p className="text-3xl font-bold">{overdueCount}</p>
              </div>
              <div className="p-3 rounded-lg bg-red-500/20"><Calendar className="h-6 w-6 text-red-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("customers", "Customers")}</p>
                <p className="text-3xl font-bold">{customers.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-500/20"><User className="h-6 w-6 text-purple-500" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search invoices..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatus", "All Status")}</SelectItem>
                <SelectItem value="draft">{t("draft", "Draft")}</SelectItem>
                <SelectItem value="posted">{t("posted", "Posted")}</SelectItem>
                <SelectItem value="paid">{t("paid", "Paid")}</SelectItem>
                <SelectItem value="voided">{t("voided", "Voided")}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchInvoices}><RefreshCw className="h-4 w-4" /></Button>
          </div>

          {loading ?
          <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500" />
            </div> :
          invoices.length === 0 ?
          <div className="text-center py-12">
              <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">{t("noInvoicesFound", "No invoices found")}</p>
            </div> :

          <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left p-3 font-medium">{t("invoice", "Invoice #")}</th>
                    <th className="text-left p-3 font-medium">{t("customer", "Customer")}</th>
                    <th className="text-left p-3 font-medium">{t("issueDate", "Issue Date")}</th>
                    <th className="text-left p-3 font-medium">{t("dueDate", "Due Date")}</th>
                    <th className="text-left p-3 font-medium">{t("total", "Total")}</th>
                    <th className="text-left p-3 font-medium">{t("status", "Status")}</th>
                    <th className="text-right p-3 font-medium">{t("actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {invoices.map((inv) =>
                <tr key={inv.id} className="hover:bg-slate-800/30">
                      <td className="p-3 font-medium">{inv.invoice_no}</td>
                      <td className="p-3">{inv.customer_name || `Customer #${inv.customer_id}`}</td>
                      <td className="p-3">{inv.issue_date}</td>
                      <td className="p-3">{inv.due_date}</td>
                      <td className="p-3 font-medium">${(inv.total || 0).toLocaleString()}</td>
                      <td className="p-3"><Badge className={statusColors[inv.status]}>{inv.status}</Badge></td>
                      <td className="p-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => viewDetails(inv.id)}>
                              <Eye className="h-4 w-4 mr-2" />{t("viewDetails", "View Details")}
                        </DropdownMenuItem>
                            {inv.status === 'draft' &&
                        <DropdownMenuItem onClick={() => handlePost(inv.id)}>
                                <Send className="h-4 w-4 mr-2 text-green-500" />{t("post", "Post")}
                        </DropdownMenuItem>
                        }
                            {inv.status !== 'voided' &&
                        <DropdownMenuItem onClick={() => handleVoid(inv.id)} className="text-red-400">
                                <Trash2 className="h-4 w-4 mr-2" />{t("void", "Void")}
                        </DropdownMenuItem>
                        }
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                )}
                </tbody>
              </table>
            </div>
          }
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("newArInvoice", "New AR Invoice")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("customer", "Customer *")}</Label>
                <Select value={formData.customer_id} onValueChange={(v) => setFormData({ ...formData, customer_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>
                    {customers.map((c) =>
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("invoiceType", "Invoice Type")}</Label>
                <Select value={formData.invoice_type} onValueChange={(v) => setFormData({ ...formData, invoice_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">{t("standard", "Standard")}</SelectItem>
                    <SelectItem value="credit_note">{t("creditNote", "Credit Note")}</SelectItem>
                    <SelectItem value="debit_note">{t("debitNote", "Debit Note")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>{t("issueDate", "Issue Date *")}</Label>
                <Input type="date" value={formData.issue_date} onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })} />
              </div>
              <div>
                <Label>{t("dueDate", "Due Date *")}</Label>
                <Input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} />
              </div>
              <div>
                <Label>{t("currency", "Currency")}</Label>
                <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">{t("usd", "USD")}</SelectItem>
                    <SelectItem value="EUR">{t("eur", "EUR")}</SelectItem>
                    <SelectItem value="GBP">{t("gbp", "GBP")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>{t("invoiceLines", "Invoice Lines")}</Label>
                <Button variant="outline" size="sm" onClick={addLine}><Plus className="h-4 w-4 mr-1" />{t("addLine", "Add Line")}</Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="text-left p-2">{t("description", "Description")}</th>
                      <th className="text-left p-2 w-20">{t("qty", "Qty")}</th>
                      <th className="text-left p-2 w-28">{t("unitPrice", "Unit Price")}</th>
                      <th className="text-left p-2 w-20">{t("tax", "Tax %")}</th>
                      <th className="text-left p-2 w-28">{t("amount", "Amount")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {formData.lines.map((line, idx) =>
                    <tr key={idx}>
                        <td className="p-2">
                          <Input value={line.description} onChange={(e) => updateLine(idx, 'description', e.target.value)} placeholder="Description" />
                        </td>
                        <td className="p-2">
                          <Input type="number" value={line.quantity} onChange={(e) => updateLine(idx, 'quantity', e.target.value)} />
                        </td>
                        <td className="p-2">
                          <Input type="number" value={line.unit_price} onChange={(e) => updateLine(idx, 'unit_price', e.target.value)} />
                        </td>
                        <td className="p-2">
                          <Input type="number" value={line.tax_rate} onChange={(e) => updateLine(idx, 'tax_rate', e.target.value)} />
                        </td>
                        <td className="p-2 font-medium">
                          ${((parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0)).toLocaleString()}
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-slate-800/30">
                    <tr>
                      <td colSpan="4" className="p-2 text-right font-medium">{t("subtotal", "Subtotal:")}</td>
                      <td className="p-2 font-bold">${subtotal.toLocaleString()}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div>
              <Label>{t("notes", "Notes")}</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Invoice notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={handleCreate} className="bg-green-600 hover:bg-green-700">{t("createInvoice", "Create Invoice")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={!!showDetails} onOpenChange={() => setShowDetails(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t("invoiceDetails", "Invoice Details")}</DialogTitle></DialogHeader>
          {showDetails &&
          <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("invoice", "Invoice #")}</p>
                  <p className="font-bold">{showDetails.invoice_no}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("customer", "Customer")}</p>
                  <p className="font-medium">{showDetails.customer_name}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("status", "Status")}</p>
                  <Badge className={statusColors[showDetails.status]}>{showDetails.status}</Badge>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                  <p className="text-sm text-muted-foreground">{t("total", "Total")}</p>
                  <p className="text-2xl font-bold text-green-400">${(showDetails.total || 0).toLocaleString()}</p>
                </div>
                <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                  <p className="text-sm text-muted-foreground">{t("paid", "Paid")}</p>
                  <p className="text-2xl font-bold text-blue-400">${(showDetails.amount_paid || 0).toLocaleString()}</p>
                </div>
                <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <p className="text-sm text-muted-foreground">{t("due", "Due")}</p>
                  <p className="text-2xl font-bold text-amber-400">${(showDetails.amount_due || 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
          }
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetails(null)}>{t("close", "Close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};

export default ARInvoicesPage;